import { useEffect, useState } from "react";
import { Sparkles, X, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Sprint 8 — Persistent Pro trial banner. Shows a countdown when the user
 * is in an active trial. Dismissible per-session (localStorage) so it isn't
 * annoying, but reappears the next day so the reminder stays visible until
 * the trial ends or the user upgrades.
 */
export default function TrialBanner() {
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/plan/status");
        setStatus(data);
      } catch { /* silent */ }
    })();
    try {
      const key = `trial_banner_dismissed_${new Date().toISOString().slice(0, 10)}`;
      if (localStorage.getItem(key) === "1") setDismissed(true);
    } catch { /* silent */ }
  }, []);

  if (!status || !status.trial_active || dismissed) return null;

  const daysLeft = status.trial_days_left ?? 0;
  const urgent = daysLeft <= 3;

  const dismiss = () => {
    try {
      const key = `trial_banner_dismissed_${new Date().toISOString().slice(0, 10)}`;
      localStorage.setItem(key, "1");
    } catch { /* silent */ }
    setDismissed(true);
  };

  const upgrade = () => { window.location.href = "/pricing"; };

  return (
    <div
      data-testid="trial-banner"
      data-days-left={daysLeft}
      className={`w-full ${urgent ? "bg-[#E9A94E]/15 border-[#E9A94E]/40" : "bg-[#00F2F4]/10 border-[#00F2F4]/30"} border-b`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-2.5 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm text-[var(--fg-1)] font-sans">
          <Sparkles size={14} className={urgent ? "text-[#E9A94E]" : "text-[#00F2F4]"} />
          <span>
            Sei in <strong>trial Pro</strong>. Mancano <strong data-testid="trial-days-left">{daysLeft}</strong>{" "}
            {daysLeft === 1 ? "giorno" : "giorni"}.
            {urgent && " Aggiungi una carta per non perdere le funzioni AI."}
          </span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={upgrade}
            data-testid="trial-banner-upgrade"
            className="inline-flex items-center gap-1.5 text-xs font-sans font-semibold rounded-full bg-[#00F2F4] hover:bg-[#00D6D8] text-[#0A0A0A] px-4 py-1.5"
            style={{ transition: "background-color 0.2s ease" }}
          >
            Aggiungi carta <ArrowRight size={12} />
          </button>
          <button
            onClick={dismiss}
            data-testid="trial-banner-dismiss"
            className="text-[var(--fg-3)] hover:text-[var(--fg-1)]"
            style={{ transition: "color 0.2s ease" }}
            title="Nascondi per oggi"
          >
            <X size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
